import { type Message } from '@ag-ui/core';
import { create } from 'zustand';

import { EMPTY_NODES } from '../consts';
import { type CanvasTokenNode } from './slices/createCanvasTokenNodeSlice';
import { type ChatSlice, createChatSlice } from './slices/createChatSlice';

const EMPTY_MESSAGES: Message[] = [];

export type ChatState = ChatSlice;

export const useChatStore = create<ChatState>()((...args) => ({
  ...createChatSlice()(...args),
}));

/**
 * Returns messages for a given thread from the runtime cache.
 * Returns an empty array if the thread has no cached data.
 */
export const useThreadMessages = (threadId: string | null) => {
  return useChatStore((state) => (threadId ? (state.threads[threadId]?.messages ?? EMPTY_MESSAGES) : EMPTY_MESSAGES));
};

/**
 * Returns agent state for a given thread from the runtime cache.
 */
export const useThreadState = (threadId: string | null) => {
  return useChatStore((state) => (threadId ? (state.threads[threadId]?.state ?? null) : null));
};

/**
 * Extracts mediaMap from the current thread's agent state.
 * Since only the active thread has a mediaMap populated, this scans all threads
 * and returns the first non-empty one found.
 */
export function getMediaMapNodes(state: ChatSlice): Record<string, CanvasTokenNode> {
  for (const thread of Object.values(state.threads)) {
    if (thread.state?.mediaMap) {
      return thread.state.mediaMap;
    }
  }
  return EMPTY_NODES;
}

export const useMediaMapNodes = () => {
  return useChatStore(getMediaMapNodes);
};
